/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/MembersRegion/ProjectMembers" />
import { FocusZone } from "OfficeFabric/FocusZone";
import * as React from "react";
import { getId } from "VSS/Core/Util/Object";
import { format } from "VSS/Core/Util/String";
import { Card } from "VSSUI/Card";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { Spinner } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

import * as ProjectOverviewResources from "TFS/Views/ProjectOverviewPage/Resources";
import { StoreLoadingState } from "TFS/Views/ProjectOverviewPage/Shared/Contracts";
import { MemberInfo } from "TFS/Views/ProjectOverviewPage/Shared/Models";
import { ProjectMembersActionCreator } from "TFS/Views/ProjectOverviewPage/Shared/ProjectMembersActionCreator";
import { ProjectMembersStore, ProjectMembersStoreState } from "TFS/Views/ProjectOverviewPage/Shared/ProjectMembersStore";

export interface ProjectMembersProps extends IVssComponentProperties {
    headingLevel: number;
    actionCreator: ProjectMembersActionCreator;
    store: ProjectMembersStore;
}

export interface ProjectMembersState {
    projectMembers: ProjectMembersStoreState;
}

const maxMembersToDisplay = 8;

export class ProjectMembers extends VssComponent<ProjectMembersProps, ProjectMembersState> {
    private _membersSectionTitleId: string;

    constructor(props: ProjectMembersProps, context: IVssComponentContext) {
        super(props, context);

        this._membersSectionTitleId = getId("membersSectionTitleId");

        this.state = {
            projectMembers: this.props.store.getState()
        };
    }

    public render(): JSX.Element {
        const { hasMore, membersCount } = this.state.projectMembers;
        const { headingLevel, className } = this.props;

        let membersPaneSubTitle = "";
        if (membersCount > 0) {
            membersPaneSubTitle = format("{0}{1}", membersCount, hasMore ? "+" : "");
        }

        return (
            <Card className={"project-overview-card"}>
                <div className={css("project-members", className)} role="region" aria-labelledby={this._membersSectionTitleId}>
                    <TitleBar
                        title={ProjectOverviewResources.ProjectMembers_Title}
                        subTitle={membersPaneSubTitle}
                        headingLevel={headingLevel}
                        id={this._membersSectionTitleId}
                    />
                    {this._getProjectMembersContent()}
                </div>
            </Card>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();

        this.props.store.addChangedListener(this._onStoreChange);
        this.props.actionCreator.fetchProjectMembers(true);
    }

    public componentWillUnmount(): void {
        this.props.store.removeChangedListener(this._onStoreChange);

        super.componentWillUnmount();
    }

    private _getProjectMembersContent(): JSX.Element | null {
        const { errorMessage, membersToDisplay, loadingState } = this.state.projectMembers;
        switch (loadingState) {
            case StoreLoadingState.Loading:
                return <Spinner />;

            case StoreLoadingState.LoadFailed:
                return <MessageBar severity={MessageBarSeverity.Error}>{errorMessage}</MessageBar>;

            case StoreLoadingState.Loaded:
                return (
                    <div className="project-members-list-container">
                        <MembersList pageContext={this.context.pageContext} membersToDisplay={membersToDisplay} />
                    </div>
                );

            default:
                return null;
        }
    }

    private _onStoreChange = (): void => {
        this.setState({ projectMembers: this.props.store.getState() });
    };
}

const TitleBar = (props: { title: string; subTitle: string; headingLevel: number; id: string }): JSX.Element => {
    const { headingLevel, title, subTitle, id } = props;

    return (
        <div className="project-members-title">
            <div className="title title-m" role="heading" aria-level={headingLevel} id={id}>
                {title}
            </div>
            <div className="sub-title font-size-ms">{subTitle}</div>
        </div>
    );
};

const MembersList = (props: { membersToDisplay: MemberInfo[]; pageContext: IVssPageContext }): JSX.Element => {
    return (
        <FocusZone className="project-members-list">
            {props.membersToDisplay.map(
                (member: MemberInfo): JSX.Element => {
                    return (
                        <LegacyComponent
                            wrappedType="TFS.ProjectOverview.ProjectMember"
                            modules={["ProjectOverview/Scripts/Components/ProjectMember"]}
                            key={member.id}
                            member={member}
                        />
                    );
                }
            )}
        </FocusZone>
    );
};
